import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, Clock, ShieldCheck, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const services = [
  {
    id: 'oven',
    name: 'Oven, Range or Stove',
    image: 'https://images.pexels.com/photos/29080604/pexels-photo-29080604.jpeg?auto=compress&cs=tinysrgb&w=800',
    serviceId: '1697132211443x112686808887197700',
  },
  {
    id: 'refrigerator',
    name: 'Refrigerator or Freezer',
    image: 'https://images.pexels.com/photos/2343467/pexels-photo-2343467.jpeg?auto=compress&cs=tinysrgb&w=800',
    serviceId: '1697131454753x470138417414668300',
  },
  {
    id: 'dishwasher',
    name: 'Dishwasher',
    image: 'https://images.pexels.com/photos/3829549/pexels-photo-3829549.jpeg?auto=compress&cs=tinysrgb&w=800',
    serviceId: '1724414703177x278134466012439200',
  },
  {
    id: 'disposal',
    name: 'Garbage Disposal',
    image: 'https://images.pexels.com/photos/6375558/pexels-photo-6375558.jpeg?auto=compress&cs=tinysrgb&w=800',
    serviceId: '1700878787321x804045750988087600',
  },
  {
    id: 'washer',
    name: 'Washer',
    image: 'https://images.pexels.com/photos/4440652/pexels-photo-4440652.jpeg?auto=compress&cs=tinysrgb&w=800',
    serviceId: '1697122887634x335859285365620740',
  },
  {
    id: 'dryer',
    name: 'Dryer',
    image: 'https://images.pexels.com/photos/5591463/pexels-photo-5591463.jpeg?auto=compress&cs=tinysrgb&w=800',
    serviceId: '1697130679646x469888136453292000',
  },
];

const ZenbookerWidget = ({ serviceId }) => {
  useEffect(() => {
    // Load Zenbooker CSS
    const existingLink = document.querySelector('link[href*="zenbooker.css"]');
    if (!existingLink) {
      const linkEl = document.createElement('link');
      linkEl.href = 'https://cdn.zenbooker.com/widget/latest/zenbooker.css';
      linkEl.rel = 'stylesheet';
      document.head.appendChild(linkEl);
    }

    // Load Zenbooker JS
    const existingScript = document.querySelector('script[src*="zenbooker.js"]');
    if (!existingScript) {
      const scriptEl = document.createElement('script');
      scriptEl.src = 'https://cdn.zenbooker.com/widget/latest/zenbooker.js';
      scriptEl.async = true;
      document.body.appendChild(scriptEl);
    }
  }, []);

  const widgetUrl = `https://widget.zenbooker.com/book/1697122450472x556377720512791740?embed=true&preselected-service=${serviceId}`;

  return (
    <div data-testid="zenbooker-widget" className="w-full">
      <div 
        className="zen-wrapper" 
        style={{
          width: '100%',
          boxShadow: '0px 4px 24px rgba(0,0,0,0.08)',
          border: '1px solid rgb(226, 226, 226)',
          margin: 'auto',
          maxWidth: '1200px',
          overflow: 'hidden',
          position: 'relative',
          borderRadius: '16px'
        }}
      >
        <div 
          className="zenbooker-inline-widget" 
          data-url={widgetUrl}
          style={{
            minWidth: '320px',
            height: '600px',
            border: '0',
            verticalAlign: 'bottom',
            borderRadius: '16px'
          }}
        />
      </div>
    </div>
  );
};

const BookingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedService, setSelectedService] = useState(null);

  // Check URL parameter on mount
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    if (serviceParam) {
      const foundService = services.find(s => s.id === serviceParam);
      if (foundService) {
        setSelectedService(foundService);
      }
    }
  }, [searchParams]);

  const handleSelectService = (service) => {
    setSelectedService(service);
    setSearchParams({ service: service.id });
    // Scroll to top when widget loads
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setSelectedService(null);
    setSearchParams({});
  };

  return (
    <main data-testid="booking-page" className="pt-24 pb-20">
      <SEO 
        title={selectedService 
          ? `Book ${selectedService.name} Repair | BBM Appliances PA & NJ` 
          : 'Book Appliance Repair Online | Same-Day Service | BBM Appliances'}
        description="Schedule appliance repair online in PA & NJ. Same-day appointments for washer, dryer, refrigerator, oven repair. Service call fee varies by brand & model. Professional technicians. Book now!"
        canonical="https://www.bbmappliances.com/book"
        keywords="book appliance repair, schedule appliance repair, online appliance booking, same day repair, washer repair appointment, dryer repair booking, Philadelphia appliance repair"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            Easy Online Booking
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {selectedService ? selectedService.name : 'Choose Your Appliance Type'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {selectedService 
              ? 'Complete your booking below. Select a convenient time and our professional technicians will be there.'
              : 'Select the appliance you need repaired to get started with your booking.'
            }
          </p>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span>Same-Day Available</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <span>Professional Service</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-5 h-5 text-primary" />
            <span>Service Call Fee (varies by brand & model)</span>
          </div>
        </div>

        {/* Service Selection or Widget */}
        {!selectedService ? (
          // Appliance Selection Grid
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8 max-w-6xl mx-auto mb-12">
            {services.map((service) => (
              <button
                key={service.id}
                data-testid={`select-${service.id}`}
                onClick={() => handleSelectService(service)}
                className="group flex flex-col items-center text-center"
              >
                <div className="relative w-full aspect-square mb-4 rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 group-hover:border-primary group-hover:shadow-xl transition-all duration-300">
                  <img 
                    src={service.image} 
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300"></div>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-foreground group-hover:text-primary transition-colors leading-tight font-heading">
                  {service.name}
                </h3>
              </button>
            ))}
          </div>
        ) : (
          // Zenbooker Widget
          <div className="mb-12">
            <Button
              data-testid="back-to-selection"
              variant="outline"
              onClick={handleBack}
              className="mb-6 rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Choose Different Appliance
            </Button>
            <ZenbookerWidget serviceId={selectedService.serviceId} />
          </div>
        )}

        {/* Alternative contact */}
        <div className="text-center bg-secondary/50 rounded-2xl p-8">
          <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
            Prefer to Call?
          </h3>
          <p className="text-muted-foreground mb-4">
            Our call center is available 7AM - 7PM, 7 days a week.
          </p>
          <a 
            href="tel:+18556056034"
            data-testid="booking-phone"
            className="inline-flex items-center gap-2 text-2xl font-bold text-primary hover:opacity-80 transition-opacity"
          >
            <Phone className="w-6 h-6" />
            (855) 605-6034
          </a>
        </div>
      </div>
    </main>
  );
};

export default BookingPage;
